
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo } from "react";
import { CheckCircle2, Circle, Clock, MoreHorizontal, ShieldCheck, Lock, Loader2, CheckCircle, Filter } from "lucide-react";
import { EscrowContract, EscrowMilestone } from "../../types/escrow";
import { useWallet } from "../../hooks/useWallet";
import { Button } from "../ui/Button";
import { ChainIcon } from "../ui/ChainIcons";
import { fetchSolanaEscrows, fetchSuiEscrows } from '../../services/onchain';
import { motion, AnimatePresence } from 'framer-motion';
import { NewEscrowModal } from './escrow/NewEscrowModal';

interface TimelineItemProps {
  milestone: EscrowMilestone;
  isLast?: boolean;
}

const TimelineItem: React.FC<TimelineItemProps> = ({ milestone, isLast }) => {
  const { title, status, date } = milestone;
  
  const getColor = () => {
    if (status === 'released') return 'text-emerald-500 border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/50';
    if (status === 'approved' || status === 'funded') return 'text-[#0A0B0F] border-[#0A0B0F] bg-white dark:text-white dark:border-white dark:bg-white/10';
    if (status === 'disputed') return 'text-red-500 border-red-500 bg-red-50 dark:bg-red-500/10 dark:border-red-500/50';
    return 'text-gray-300 border-gray-300 bg-transparent dark:text-gray-700 dark:border-gray-800';
  };

  const getLineColor = () => {
      if (status === 'released') return 'bg-emerald-500';
      return 'bg-gray-200 dark:bg-gray-800';
  }

  return (
    <div className="flex gap-4 group/item">
       <div className="flex flex-col items-center">
          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${getColor()} z-10 transition-all duration-300 shadow-sm`}>
             {status === 'released' ? <CheckCircle2 size={12} strokeWidth={3}/> : (status === 'approved' || status === 'funded') ? <Clock size={12} strokeWidth={3}/> : <Circle size={12}/>}
          </div>
          {!isLast && <div className={`w-0.5 flex-1 my-1 rounded-full ${getLineColor()}`}></div>}
       </div>
       <div className={`pb-6 ${status === 'pending' ? 'opacity-50' : 'opacity-100'} pt-0.5`}>
          <div className="font-bold text-[#0A0B0F] dark:text-gray-200 text-sm group-hover/item:text-emerald-600 dark:group-hover/item:text-emerald-400 transition-colors">{title}</div>
          {date && <div className="text-[10px] text-gray-500 mt-1 font-mono">{date}</div>}
       </div>
    </div>
  );
};

export const EscrowView: React.FC = () => {
  const { walletAddress } = useWallet();
  const [contracts, setContracts] = useState<EscrowContract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewContract, setShowNewContract] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [filter, setFilter] = useState<'ALL' | 'SOL' | 'SUI'>('ALL');

  useEffect(() => {
    const loadEscrows = async () => {
      if (!walletAddress) {
        setContracts([]);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const [suiEscrows, solanaEscrows] = await Promise.all([
          fetchSuiEscrows(walletAddress),
          fetchSolanaEscrows(walletAddress),
        ]);
        setContracts([...suiEscrows, ...solanaEscrows].sort((a, b) => b.createdAt - a.createdAt));
      } catch (error) {
        console.error("Failed to fetch escrows:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadEscrows();
  }, [walletAddress]);

  const filteredContracts = useMemo(() => {
      if (filter === 'ALL') return contracts;
      return contracts.filter(c => c.chain === filter);
  }, [contracts, filter]);
  
  const handleContractCreated = () => {
      setShowNewContract(false);
      setShowSuccess(true);
      // Refresh logic would go here
      setTimeout(() => setShowSuccess(false), 4000);
  };

  const handleAction = (contractId: string, milestoneIndex: number, action: 'approve' | 'release') => {
      if (!walletAddress) {
          alert("Connect wallet to sign transaction.");
          return;
      }
      setContracts(prev => prev.map(c => {
          if (c.id !== contractId) return c;
          const newMilestones = [...c.milestones];
          const currentStatus = newMilestones[milestoneIndex].status;

          if (action === 'approve' && currentStatus === 'funded') {
              newMilestones[milestoneIndex].status = 'approved';
          } else if (action === 'release' && currentStatus === 'approved') {
              newMilestones[milestoneIndex].status = 'released';
              newMilestones[milestoneIndex].released = true;
          }
          
          return { ...c, milestones: newMilestones };
      }));
  };

  return (
    <div className="space-y-8 animate-fade-in">
       <div className="flex flex-col md:flex-row justify-between items-center md:items-end gap-6 text-center md:text-left px-4 sm:px-0">
          <div className="space-y-1">
            <h2 className="font-serif text-4xl text-[#0A0B0F] dark:text-white font-bold tracking-tight">Escrow Service</h2>
            <p className="text-stone-500 text-sm">Secure smart contract agreements.</p>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="flex p-1 bg-stone-100 dark:bg-white/5 rounded-xl border border-stone-200 dark:border-white/10 shrink-0">
                {(['ALL', 'SOL', 'SUI'] as const).map(n => (
                    <button 
                        key={n} 
                        onClick={() => setFilter(n)}
                        className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${filter === n ? 'bg-[#0A0B0F] dark:bg-white text-white dark:text-black shadow-md' : 'text-stone-400 hover:text-stone-600'}`}
                    >
                        {n}
                    </button>
                ))}
            </div>
            <button onClick={() => setShowNewContract(true)} className="flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-[#0A0B0F] dark:bg-white text-white dark:text-[#0A0B0F] rounded-btn hover:opacity-90 transition-opacity font-bold text-xs uppercase tracking-widest shadow-smooth whitespace-nowrap">
               <ShieldCheck size={16} /> New Contract
            </button>
          </div>
       </div>
        
       {isLoading && (
         <div className="flex flex-col items-center justify-center py-20 text-stone-400 gap-4">
            <Loader2 className="animate-spin text-nobel-gold" size={40} />
            <span className="text-[10px] font-black uppercase tracking-[0.3em]">Loading contracts...</span>
         </div>
       )}

       {!isLoading && filteredContracts.length === 0 && (
         <div className="flex flex-col items-center justify-center text-center py-20 border border-dashed border-gray-300 dark:border-white/10 rounded-card bg-stone-50/50 dark:bg-white/[0.01]">
            <div className="w-20 h-20 bg-stone-100 dark:bg-white/5 rounded-3xl flex items-center justify-center text-stone-300 dark:text-stone-700 mb-6 shadow-inner">
                <ShieldCheck size={40} />
            </div>
            <h3 className="font-serif text-2xl font-bold text-[#0A0B0F] dark:text-white">No Active {filter !== 'ALL' ? filter : ''} Contracts</h3>
            <p className="text-sm text-stone-500 mt-2 max-w-xs mx-auto">Connect your wallet or create a new contract on the {filter === 'ALL' ? 'main' : filter} network.</p>
         </div>
       )}
       
       {!isLoading && filteredContracts.length > 0 && (
          <div className="grid grid-cols-1 gap-8">
              {filteredContracts.map(contract => (
                 <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    key={contract.id} 
                    className="bg-white dark:bg-gradient-to-b dark:from-[#15171C] dark:to-[#0A0B0F] rounded-card border border-gray-100 dark:border-[#2D3142] p-6 md:p-8 shadow-smooth dark:shadow-smooth-dark hover:shadow-xl transition-all duration-500 group relative overflow-hidden"
                >
                     <div className="flex flex-col sm:flex-row justify-between items-start mb-8 border-b border-gray-50 dark:border-white/5 pb-6 relative z-10">
                         <div className="flex gap-5">
                            <div className="relative">
                                <div className="w-14 h-14 bg-stone-50 dark:bg-white/5 rounded-xl flex items-center justify-center text-[#0A0B0F] dark:text-white border border-gray-100 dark:border-white/5 shadow-inner group-hover:bg-nobel-gold group-hover:text-black transition-colors duration-500">
                                    <Lock size={24} />
                                </div>
                                <div className="absolute -bottom-1 -right-1">
                                    <ChainIcon chain={contract.chain} className="w-6 h-6 border-2 border-white dark:border-[#15171C] rounded-full shadow-lg" />
                                </div>
                            </div>
                            <div className="text-left">
                                <div className="flex flex-wrap items-center gap-3 mb-1.5">
                                    <h3 className="font-serif text-2xl font-bold text-[#0A0B0F] dark:text-white tracking-tight">{contract.title}</h3>
                                    <div className="px-3 py-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-black uppercase tracking-[0.2em] rounded-md border border-emerald-500/20 shadow-sm">
                                        {contract.status}
                                    </div>
                                </div>
                                <div className="text-[10px] text-stone-400 font-mono font-bold tracking-widest truncate max-w-xs opacity-60">REF: {contract.id.toUpperCase()}</div>
                            </div>
                         </div>
                         <button className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-stone-50 dark:hover:bg-white/10 text-stone-400 hover:text-[#0A0B0F] dark:hover:text-white transition-all mt-4 sm:mt-0 border border-transparent hover:border-stone-200">
                            <MoreHorizontal size={20} />
                         </button>
                     </div>

                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 relative z-10 text-left">
                         <div className="p-6 bg-stone-50/50 dark:bg-white/[0.02] rounded-xl border border-stone-100 dark:border-white/5 shadow-inner">
                             <div className="text-[10px] uppercase tracking-[0.3em] text-stone-400 font-black mb-3">Contract Value</div>
                             <div className="flex items-baseline gap-2">
                                <span className="text-3xl font-serif text-[#0A0B0F] dark:text-white font-bold tracking-tighter">${contract.total.toLocaleString()}</span>
                                <span className="text-stone-400 font-mono text-sm font-bold">{contract.token}</span>
                             </div>
                         </div>
                         <div className="p-6 bg-stone-50/50 dark:bg-white/[0.02] rounded-xl border border-stone-100 dark:border-white/5 shadow-inner">
                             <div className="text-[10px] uppercase tracking-[0.3em] text-stone-400 font-black mb-3">Network</div>
                             <div className="flex items-center gap-3">
                                 <ChainIcon chain={contract.chain} className="w-5 h-5" />
                                 <span className="text-sm font-black text-[#0A0B0F] dark:text-stone-300 uppercase tracking-widest">
                                     {contract.chain === 'SOL' ? 'Solana' : 'Sui Network'}
                                 </span>
                             </div>
                             <div className="mt-2 text-[10px] text-stone-500 font-mono">{contract.txHash.slice(0, 24)}...</div>
                         </div>
                     </div>

                     <div className="space-y-0 pl-1 relative z-10 mb-8 text-left max-w-3xl">
                        <div className="text-[10px] font-black text-stone-400 uppercase tracking-[0.3em] mb-6 flex items-center gap-2">
                            <Clock size={12} /> Timeline
                        </div>
                        {contract.milestones.map((m, i) => (
                            <div key={m.index} className="flex justify-between items-start group/ms">
                                <TimelineItem 
                                    milestone={m}
                                    isLast={i === contract.milestones.length - 1} 
                                />
                                <div className="pt-1 flex gap-2">
                                    <div className="text-right mr-4 hidden sm:block">
                                        <div className="text-sm font-mono font-bold text-stone-900 dark:text-stone-200">{m.amount.toLocaleString()} {contract.token}</div>
                                        <div className="text-[9px] text-stone-400 font-bold uppercase tracking-widest">Allocation</div>
                                    </div>
                                    {m.status === 'funded' && <Button size="sm" variant="outline" className="h-9 px-5 text-[10px] font-black uppercase tracking-widest rounded-sm" onClick={() => handleAction(contract.id, i, 'approve')}>Approve</Button>}
                                    {m.status === 'approved' && <Button size="sm" variant="primary" className="h-9 px-5 text-[10px] font-black uppercase tracking-widest rounded-sm shadow-md" onClick={() => handleAction(contract.id, i, 'release')}>Release</Button>}
                                </div>
                            </div>
                        ))}
                     </div>
                     
                     <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-stone-200 dark:via-white/10 to-transparent opacity-50"></div>
                 </motion.div>
              ))}
           </div>
       )}

      <AnimatePresence>
          {showNewContract && (
              <NewEscrowModal
                  onClose={() => setShowNewContract(false)}
                  onComplete={handleContractCreated}
              />
          )}
      </AnimatePresence>

      <AnimatePresence>
          {showSuccess && (
              <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="fixed bottom-10 right-10 bg-[#0A0B0F] text-white px-8 py-5 rounded-[24px] shadow-2xl flex items-center gap-4 z-50 border border-nobel-gold/50"
              >
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                    <CheckCircle size={20} className="text-white" strokeWidth={3} />
                  </div>
                  <div>
                    <div className="font-bold">Contract Created</div>
                    <div className="text-[10px] text-stone-400 font-mono">Consensus reached on {filter === 'ALL' ? 'Mainnet' : filter}</div>
                  </div>
              </motion.div>
          )}
      </AnimatePresence>
    </div>
  );
};
